import React, {useEffect, useImperativeHandle, useMemo} from 'react';
import Draggable from 'react-draggable';
import '../../../../assets/styles/BRandRBBItem.css';
import {ChildrenBlur} from '../../../../components/ChildrenBlur';
import TemperatureBackground from '../../../../components/TemperatureBackground';
import {ItemInput, ItemMenu} from './ItemUtils';

function calcPosition(position, size, scale, broadcaster, itemType) {
  if (broadcaster === 'RBB' && itemType === 'br-temperature') {
    const xPos = position.x * scale;
    const yPos = position.y * scale - (size.height * scale) / 2;
    return {
      x: xPos,
      y: yPos
    };
  } else {
    const xPos = position.x * scale - (size.width * scale) / 2;
    const yPos = position.y * scale - (size.height * scale) / 2;

    return {
      x: xPos,
      y: yPos
    };
  }
}

const getOutlineColor = (itemType, opacity) => {
  if (itemType === 'br-icon') {
    return 'rgb(148,0,255,' + opacity + ')';
  } else if (itemType === 'br-temperature') {
    return 'rgb(0,255,222,' + opacity + ')';
  } else if (itemType === 'br-danger') {
    return 'rgb(255,128,0,' + opacity + ')';
  }
  return 'transparent';
};

const BRItem = ({childRef, item, ...props}) => {
  //LAYOUT
  const [opacity, setOpacity] = React.useState(0.65);
  const hasUnit = typeof item.ui.unit !== 'undefined';

  const [position, setPosition] = React.useState(
    calcPosition(
      props.initialPosition,
      item.size,
      props.mapScale,
      props.data.broadcaster,
      item.type
    )
  );

  const [locationLocked, setLocationLocked] = React.useState(true);

  const [hidden, setHidden] = React.useState(props.hidden);

  const [dragged, setDragged] = React.useState(false);
  const [isFocusInElement, setIsFocusInElement] = React.useState(false);
  const [isMouseInside, setIsMouseInside] = React.useState(false);
  const [isRenderView, setIsRenderView] = React.useState(props.renderView);
  const [showMenu, setShowMenu] = React.useState(false);

  const valueKey = Object.keys(item.data)[1];
  const itemValue = item.inherited
    ? require('json-pointer').get(props.parentData, item.parent)
    : item.data[valueKey].value;

  if (item.inherited) {
    if (itemValue !== item.data[valueKey].value) {
      item.data[valueKey].value = itemValue;
    }
  }

  function updateIconBackground(item, itemValue) {
    if (itemValue === 'X' || itemValue === 'NaN') {
      return '';
    }
    return (
      'url("' +
      item.ui.folder +
      item.ui.fileName +
      itemValue +
      item.ui.fileExtension +
      '")'
    );
  }

  useEffect(() => {
    setHidden(props.hidden);
    setPosition(
      calcPosition(
        props.initialPosition,
        item.size,
        props.mapScale,
        props.data.broadcaster,
        item.type
      )
    );
  }, [
    props.initialPosition,
    props.hidden,
    props.mapScale,
    item.size,
    props.data.broadcaster,
    item.type
  ]);

  useImperativeHandle(childRef, () => ({
    check(state) {
      if (item.data.movable) {
        setLocationLocked(state);
      }
    },
    resetPosition() {
      resetPosition();
    },
    setRenderView(state) {
      setIsRenderView(state);
      if (item.data.movable && !locationLocked) {
        setLocationLocked(state);
      }
    }
  }));

  const resetPosition = () => {
    setPosition(
      calcPosition(
        item.data.defaultPosition,
        item.size,
        props.mapScale,
        props.data.broadcaster,
        item.type
      )
    );
    props.setValueInMap(item, {x: {value: item.data.defaultPosition.x}});
    props.setValueInMap(item, {y: {value: item.data.defaultPosition.y}});
  };

  const handleLock = v => {
    setLocationLocked(v);
  };

  const handleHide = v => {
    if (item.inherited) {
      return;
    }

    setHidden(v);
    props.setValueInMap(item, {hidden: {value: v}});
  };

  const onStart = () => {
    if (!locationLocked) setDragged(true);
  };
  const onStop = () => {
    if (!locationLocked) setDragged(false);
  };

  const dragHandlers = {onStart, onStop};

  const onControlledDrag = (e, position) => {
    if (locationLocked) {
      return;
    }
    setPosition(position);
  };

  const onControlledDragStop = (e, position) => {
    if (locationLocked) {
      return;
    }
    setPosition(position);
    const newX = position.x / props.mapScale + item.size.width * props.mapScale;
    const newY =
      position.y / props.mapScale + item.size.height * props.mapScale;
    props.setValueInMap(item, {x: {value: newX}});
    props.setValueInMap(item, {y: {value: newY}});

    onStop();
  };

  function handleMenuToggleClicked(e) {
    setShowMenu(e.target.checked);
  }

  function createContent(showMenu) {
    if (showMenu) {
      return (
        <ItemMenu
          item={item}
          hidden={hidden}
          locked={locationLocked}
          onReset={resetPosition}
          onLock={handleLock}
          onHide={handleHide}
        />
      );
    } else {
      if (!hidden) {
        return (
          <ItemInput
            item={item}
            inherited={item.inherited}
            data={{key: valueKey, value: itemValue}}
            callback={props.setValueInMap}
            disabled={!locationLocked}
          />
        );
      }
    }
  }

  const itemContainerClassName = useMemo(() => {
    const classNames = ['item-container'];
    if (isMouseInside && !isFocusInElement) {
      classNames.push('item-container--hover');
    }
    if (isMouseInside && isFocusInElement) {
      classNames.push('item-container--focused');
    }
    if (dragged) {
      classNames.push('item-container--dragged');
    }
    return classNames.join(' ');
  }, [isMouseInside, isFocusInElement, dragged]);

  return (
    <Draggable
      {...dragHandlers}
      onStop={onControlledDragStop}
      onDrag={onControlledDrag}
      position={position}
      disabled={!item.data.movable || locationLocked}>
      <div
        className='item-background'
        style={{
          zIndex: isMouseInside ? '2' : '0',
          width: item.size.width * props.mapScale + 'px',
          height: item.size.height * props.mapScale + 'px',
          backgroundImage:
            !hasUnit && !hidden ? updateIconBackground(item, itemValue) : '',
          backgroundColor: locationLocked
            ? 'transparent'
            : hidden
            ? 'transparent'
            : 'rgb(255,255,255,0.15)'
        }}>
        {hasUnit && !hidden && (
          <TemperatureBackground
            item={item}
            value={itemValue}
            mapScale={props.mapScale}
            broadcaster={props.data.broadcaster}
          />
        )}
        {!isRenderView && (
          <ChildrenBlur
            // This will trigger when blur leaves the element and it's children
            onBlur={() => {
              setIsFocusInElement(false);
              setShowMenu(false);
              setOpacity(0.55);
            }}
            // onFocus works as expected natively,
            // but it will be triggered for each focused child element
            onFocus={() => {
              setOpacity(1.0);
              setIsFocusInElement(true);
            }}
            onMouseLeave={() => {
              setIsMouseInside(false);
              setOpacity(0.55);
            }}
            onMouseEnter={() => {
              setIsMouseInside(true);
              setOpacity(1.0);
            }}
            className={itemContainerClassName}
            style={{
              width: item.ui.fontSize * 1.1635 + 'px',
              height: item.ui.fontSize * 1.1635 + 'px',
              outlineStyle: locationLocked
                ? item.inherited
                  ? 'double'
                  : 'solid'
                : 'dotted',

              outlineColor: getOutlineColor(item.type, opacity)
            }}>
            {(item.data.movable || item.data.hideable) && (
              <MenuToggle
                showMenu={showMenu}
                handleMenuToggleClicked={handleMenuToggleClicked}
              />
            )}
            {createContent(showMenu)}
          </ChildrenBlur>
        )}
      </div>
    </Draggable>
  );
};

const MenuToggle = ({handleMenuToggleClicked, showMenu}) => {
  const handleClick = event => {
    handleMenuToggleClicked(event);
  };
  return (
    <input
      type='checkbox'
      checked={showMenu}
      readOnly={true}
      tabIndex='-1'
      className='item-menu-toggle'
      onClick={e => handleClick(e)}
    />
  );
};
export default BRItem;
